'use client';

import { useId, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { RiTimerLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';

import { ProductSection, SectionHead } from '@/components/product-section';
import { ProgressRail } from '@/components/ui/progress-rail';
import { dur, spring, springFade } from '@/lib/motion';
import { hl } from '@/lib/rich-text';
import { AD_INTEGRATIONS } from '@/lib/site-integrations';

import './campaigns.css';

/**
 * "Bütçe kazanan kanala akar" — platformlar arası bütçe önerisi.
 *
 * Davranış ürünün kendisi (`campaign-brief.ts` ~2603, `campaign-brief.service`
 * ~680): brief'te kutu işaretlenirse her sabah 06:00'da son 7 günün ROAS'ına
 * bakan bir kural kuruluyor; TOPLAMI SABİT tutup yeni dağılım ÖNERİYOR
 * (SUGGEST — kullanıcı onaylıyor, beş platforma yazılıyor). En az iki
 * kampanya yayına girmiş olmalı. Rakamlar örnek.
 *
 * Dağılım bir ray: `ProgressRail` (DESIGN.md §3), yığılmış dilimler, her
 * platform kendi tonunda; görünüm değişince ray `key` ile yeniden kuruluyor.
 * Renk tek başına bilgi değil — alttaki satırlar tutarı ve farkı yazıyor.
 */

type Split = Record<string, number>;

const TOTAL = 300;
const NOW: Split = { meta: 120, google: 90, tiktok: 60, x: 30 };
const NEXT: Split = { meta: 150, google: 95, tiktok: 30, x: 25 };
const ROAS: Split = { meta: 3.8, google: 2.9, tiktok: 1.2, x: 1.6 };
const ORDER = ['meta', 'google', 'tiktok', 'x'] as const;

/** Dilim tonları — aynı yeşil ailesinin dört koyuluğu, sıra = ROAS sırası. */
const TONE: Record<(typeof ORDER)[number], string> = {
  meta: 'var(--aqua-green-800)',
  google: 'var(--aqua-green-600)',
  tiktok: 'var(--aqua-green-400)',
  x: 'var(--aqua-green-200)',
};

export function CampaignBudget() {
  const t = useTranslations('featuresCampaigns.budget');
  const locale = useLocale();
  const uid = useId();
  const [view, setView] = useState<'now' | 'next'>('now');
  const split = view === 'now' ? NOW : NEXT;

  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
  const ratio = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return (
    <ProductSection tone="white" labelledBy={`${uid}-title`}>
      <div className="cbg">
        <SectionHead
          id={`${uid}-title`}
          eyebrow={t('eyebrow')}
          title={t.rich('title', { hl })}
          lead={t('lead')}
        />

        <div className="cbg-card">
          <div className="cbg-top">
            <div className="cbg-switch" role="group" aria-label={t('switchLabel')}>
              {(['now', 'next'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  className="cbg-switch-btn"
                  aria-pressed={view === key}
                  aria-controls={`${uid}-rows`}
                  onClick={() => setView(key)}
                >
                  {view === key && (
                    <motion.span
                      layoutId={`${uid}-pill`}
                      className="cbg-switch-pill"
                      transition={spring.snap}
                      aria-hidden="true"
                    />
                  )}
                  <span className="cbg-switch-label">{t(`views.${key}`)}</span>
                </button>
              ))}
            </div>
            <p className="cbg-total">{t('total', { total: money.format(TOTAL) })}</p>
          </div>

          <div className="cbg-rail">
            <ProgressRail
              key={view}
              segments={ORDER.map((key) => ({ value: split[key]! / TOTAL, color: TONE[key] }))}
              label={t('railLabel', {
                parts: ORDER.map(
                  (key) =>
                    `${AD_INTEGRATIONS.find((item) => item.key === key)!.name} ${money.format(split[key]!)}`,
                ).join(', '),
              })}
            />
          </div>

          <ul className="cbg-rows" id={`${uid}-rows`} aria-live="polite">
            {ORDER.map((key) => {
              const p = AD_INTEGRATIONS.find((item) => item.key === key)!;
              const delta = NEXT[key]! - NOW[key]!;
              return (
                <li key={key} className="cbg-row">
                  <span className="cbg-swatch" style={{ background: TONE[key] }} aria-hidden="true" />
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    className="cbg-mark"
                    src={p.mark}
                    alt=""
                    width={p.w}
                    height={p.h}
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="cbg-name">{p.name}</span>
                  <span className="cbg-roas">{t('roas', { roas: ratio.format(ROAS[key]!) })}</span>
                  <span className="cbg-amount">
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.span
                        key={`${view}-${key}`}
                        className="cbg-amount-value"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0, transition: springFade(spring.snap) }}
                        exit={{ opacity: 0, y: -6, transition: { duration: dur.instant } }}
                      >
                        {money.format(split[key]!)}
                      </motion.span>
                    </AnimatePresence>
                  </span>
                  <span
                    className={`cbg-delta${delta > 0 ? ' cbg-delta--up' : delta < 0 ? ' cbg-delta--down' : ''}`}
                    data-show={view === 'next' ? '' : undefined}
                  >
                    {delta > 0 ? '+' : delta < 0 ? '−' : '±'}
                    {money.format(Math.abs(delta))}
                  </span>
                </li>
              );
            })}
          </ul>

          <p className="cbg-foot">
            <RiTimerLine size={15} aria-hidden="true" />
            {t(view === 'now' ? 'footNow' : 'footNext')}
          </p>
        </div>
      </div>

      <ul className="ps-facts">
        {(['fixed', 'min', 'approve'] as const).map((key) => (
          <li key={key} className="ps-fact">
            <p className="ps-fact-title">{t(`facts.${key}.title`)}</p>
            <p className="ps-fact-text">{t(`facts.${key}.text`)}</p>
          </li>
        ))}
      </ul>
    </ProductSection>
  );
}
